import {
  Avatar,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link as ChakraLink,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { BookOpen, Flame, Play, Video } from "lucide-react";
import QRCode from "qrcode.react";
import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AnnouncementsCarousel from "../../components/student/AnnouncementsCarousel";
import DailyChallengeMenu from "../../components/student/DailyChallengeMenu";
import MissedQuestions from "../../components/student/Cards/MissedQuestions";
import StudentPortalShell from "../../components/student/StudentPortalShell";
import { getAllStudentChaptersRequest } from "../../modules/students-modules/chapters/Actions";
import { getPerformanceAnalyticsRequest } from "../../modules/students-modules/performance/Actions";
import {
  getLiveVideoRequest,
  setLiveVideoSeenRequest,
} from "../../modules/students-modules/videos/Actions";
import { getActiveAnnouncementsRequest } from "../../modules/students-modules/announcements/Actions";
import { studentPalette } from "../../constants/studentPalette";

const C = {
  bg: studentPalette.sand,
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentPalette.stone,
  cream: studentPalette.cream,
  paper: studentPalette.paper,
  warmBeige: studentPalette.warmBeige,
  gold: studentPalette.crimson,
  muted: "rgba(91, 13, 22, 0.65)",
};

function scanAttendanceUrl(userId) {
  const raw = "https://edutopiaonline.com/";
  const base = String(raw)
    .replace(/\s/g, "")
    .replace(/['"]/g, "")
    .replace(/\/$/, "");
  return `${base}/scan-code/${userId}/attendence`;
}

const StudentHome = ({ t }) => {
  const baseUrl = "https://api.edutopiaonline.com/";
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const liveVideo = useSelector((state) => state.chapterVideos.liveVideo);
  const announcements = useSelector((state) => state.studentAnnouncements.announcements);
  const studentChapters = useSelector((state) => state.studentChapters.chapters);
  const performanceData = useSelector((state) => state.performance.analytics);
  const perfLoading = useSelector((state) => state.performance.isLoading);

  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const { isOpen: isChallengeOpen, onOpen: onChallengeOpen, onClose: onChallengeClose } = useDisclosure();

  useEffect(() => {
    dispatch(getAllStudentChaptersRequest());
    dispatch(getLiveVideoRequest());
    dispatch(getActiveAnnouncementsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (userData?.id) {
      dispatch(getPerformanceAnalyticsRequest(userData.id));
    }
  }, [dispatch, userData?.id]);

  useEffect(() => {
    const hasSeenAnnouncements = localStorage.getItem("hasSeenAnnouncements");
    if (!hasSeenAnnouncements && announcements && announcements.length > 0) {
      setShowAnnouncements(true);
    }
  }, [announcements]);

  const handleCloseAnnouncements = () => {
    setShowAnnouncements(false);
    localStorage.setItem("hasSeenAnnouncements", "true");
  };

  const monthlyBars = useMemo(() => {
    const rows = performanceData?.monthly_accuracy || [];
    const mapped = rows.map((item) => ({
      key: item.month,
      label: (() => {
        try {
          return new Date(`${item.month}-01`).toLocaleDateString("en-US", {
            month: "short",
          });
        } catch {
          return item.month;
        }
      })(),
      accuracy: Math.min(100, Math.max(0, parseFloat(String(item.accuracy)) || 0)),
    }));
    return mapped.slice(-7);
  }, [performanceData?.monthly_accuracy]);

  const contentCoverage = useMemo(() => {
    const targets = performanceData?.chapter_targets || [];
    let totalQ = 0;
    let solved = 0;
    targets.forEach((ch) => {
      totalQ += Number(ch.total_questions) || 0;
      solved += Number(ch.total_solved) || 0;
    });
    if (totalQ <= 0) return { pct: 0, solved, totalQ };
    return {
      pct: Math.round((solved / totalQ) * 100),
      solved,
      totalQ,
    };
  }, [performanceData?.chapter_targets]);

  const missedVideos = useMemo(() => {
    const raw = performanceData?.missed_videos;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return Object.values(raw);
    return [];
  }, [performanceData?.missed_videos]);
  const totalSolved = performanceData?.total_questions_solved ?? 0;

  const modulePreview = useMemo(() => {
    const list = Array.isArray(studentChapters) ? studentChapters : [];
    return list.slice(0, 2);
  }, [studentChapters]);

  return (
    <StudentPortalShell>
      <Box
        bg={C.bg}
        minH={{ base: "auto", md: "100vh" }}
        py={{ base: 6, md: 10 }}
        ps={{ base: 2, md: 4, lg: 3 }}
        pe={{ base: 2, md: 4, lg: 6 }}
        overflowX="hidden"
      >
        <Box
          maxW="1200px"
          mx={{ base: "auto", md: 0 }}
          me="auto"
          ps={{ base: 0, md: 3, lg: 5 }}
          w="full"
          minW={0}
        >
          {/* Top: dossier + recent */}
          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={8} w="full" minW={0}>
            <GridItem minW={0}>
              <Flex
                position="relative"
                direction={{ base: "column", sm: "row" }}
                align={{ base: "stretch", sm: "center" }}
                justify="space-between"
                gap={6}
                p={{ base: 6, md: 8 }}
                bg="white"
                borderWidth="1px"
                borderColor={C.border}
                borderRadius="lg"
                boxShadow="sm"
                w="full"
                minW={0}
              >
                {userData?.grade?.id === 10 && (
                  <Box position="absolute" top="-10px" left="-10px" zIndex={1}>
                    <Text
                      bg="yellow.400"
                      color="white"
                      fontWeight="800"
                      px={3}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      letterSpacing="0.1em"
                      textTransform="uppercase"
                      boxShadow="md"
                    >
                      Premium
                    </Text>
                  </Box>
                )}
                <Flex align="flex-start" gap={{ base: 4, md: 8 }} flex="1" minW={0}>
                  <Avatar
                    size="xl"
                    borderRadius="lg"
                    src={userData?.subject?.logo ? baseUrl + userData.subject.logo.replace(/^\//, "") : undefined}
                    name={userData?.name}
                  />
                  <Box minW={0}>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.2em"
                      color={`${C.secondary}99`}
                      textTransform="uppercase"
                      mb={1}
                    >
                      Student dossier
                    </Text>
                    <Heading
                      size="lg"
                      color={C.primary}
                      fontWeight="800"
                      mb={4}
                      fontFamily="'Tajawal','Amiri',sans-serif"
                      maxW="100%"
                      wordBreak="break-word"
                    >
                      {userData?.name}
                    </Heading>
                    <Flex gap={10} flexWrap="wrap">
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Group
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.group?.name || "—"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Subject
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.subject?.name || "—"}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
                <VStack
                  spacing={2}
                  p={4}
                  bg="rgba(235, 224, 215, 0.45)"
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  align="center"
                  minW={{ base: "full", sm: "140px" }}
                  w={{ base: "full", sm: "auto" }}
                >
                  <Box
                    bg="white"
                    p={2}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={C.border}
                  >
                    <QRCode size={112} value={scanAttendanceUrl(userData?.id)} />
                  </Box>
                  <Text fontSize="9px" fontWeight="800" color={`${C.secondary}99`} textTransform="uppercase" letterSpacing="widest">
                    Attendance QR
                  </Text>
                </VStack>
              </Flex>
            </GridItem>

            <GridItem minW={0}>
              <Box p={6} bg={C.paper} borderWidth="1px" borderColor={C.border} borderRadius="lg" h="full">
                <Text fontSize="11px" fontWeight="800" letterSpacing="0.12em" color={C.secondary} textTransform="uppercase" mb={4}>
                  Recent content
                </Text>
                <VStack align="stretch" spacing={3}>
                  {missedVideos.length === 0 ? (
                    <Text fontSize="sm" color={C.muted}>
                      No missed videos flagged yet. Keep up the great work.
                    </Text>
                  ) : (
                    missedVideos.slice(0, 2).map((v, i) => (
                      <Flex
                        key={v?.id ?? i}
                        align="center"
                        gap={3}
                        p={3}
                        bg="white"
                        borderWidth="1px"
                        borderColor={C.border}
                        borderRadius="lg"
                      >
                        <Flex
                          w="56px"
                          h="56px"
                          bg={C.secondary}
                          color={C.cream}
                          borderRadius="lg"
                          align="center"
                          justify="center"
                          direction="column"
                          flexShrink={0}
                        >
                          <Text fontSize="8px" fontWeight="800" textTransform="uppercase">
                            {v?.created_at ? new Date(v.created_at).toLocaleString("en-US", { month: "short" }) : "—"}
                          </Text>
                          <Text fontSize="xl" fontWeight="800" lineHeight="1">
                            {v?.created_at ? new Date(v.created_at).getDate() : "•"}
                          </Text>
                        </Flex>
                        <Box minW={0}>
                          <Text fontSize="sm" fontWeight="700" color={C.secondary} noOfLines={2}>
                            {v?.title || v?.name || "Video"}
                          </Text>
                          {v?.chapter?.name && (
                            <Text fontSize="xs" color={C.muted} fontStyle="italic" noOfLines={1}>
                              {v.chapter.name}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    ))
                  )}
                </VStack>
              </Box>
            </GridItem>
          </Grid>

          {/* Analytics + sidebar */}
          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={10} w="full" minW={0}>
            <GridItem minW={0}>
              <Box
                p={6}
                bg={C.secondary}
                color={C.cream}
                borderRadius="lg"
                boxShadow="lg"
                mb={6}
                w="full"
                minW={0}
              >
                <Flex justify="space-between" align="center" mb={8}>
                  <Text fontSize="11px" fontWeight="800" letterSpacing="0.12em" textTransform="uppercase">
                    Performance analytics
                  </Text>
                </Flex>
                {perfLoading && !performanceData?.monthly_accuracy?.length ? (
                  <Flex justify="center" py={10}>
                    <Spinner color={C.cream} />
                  </Flex>
                ) : (
                  <Flex align="flex-end" justify="space-between" gap={2} minH="160px" pb={6} w="full" minW={0}>
                    {monthlyBars.length === 0 ? (
                      <Text fontSize="sm" opacity={0.85}>
                        Monthly accuracy will appear after you complete graded practice.
                      </Text>
                    ) : (
                      monthlyBars.map((b) => (
                        <Flex key={b.key} flex="1" direction="column" align="center" justify="flex-end" h="140px">
                          <Box flex="1" w="full" maxH="120px" display="flex" alignItems="flex-end" position="relative">
                            <Box
                              w="full"
                              h="full"
                              bg="rgba(247,242,236,0.18)"
                              borderRadius="4px"
                              position="relative"
                              overflow="hidden"
                            >
                              <Box
                                position="absolute"
                                bottom={0}
                                left={0}
                                right={0}
                                h={`${b.accuracy}%`}
                                minH="4px"
                                bg={C.gold}
                                borderRadius="4px"
                                transition="height 0.3s ease"
                              />
                            </Box>
                          </Box>
                          <Text fontSize="9px" fontWeight="700" textTransform="uppercase" opacity={0.65} mt={2}>
                            {b.label}
                          </Text>
                        </Flex>
                      ))
                    )}
                  </Flex>
                )}
              </Box>

              <Text fontSize="sm" fontWeight="800" color={C.secondary} letterSpacing="0.08em" textTransform="uppercase" mb={4}>
                Current modules
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {modulePreview.length === 0 ? (
                  <GridItem colSpan={2}>
                    <Box p={6} bg="white" borderWidth="1px" borderColor={C.border} borderRadius="lg">
                      <Text fontSize="sm" color={C.muted}>
                        Your enrolled lessons will show here. Open sessions to get started.
                      </Text>
                      <Button as={Link} to="/students/courses" mt={4} size="sm" bg={C.border} color={C.secondary} _hover={{ bg: C.primary, color: C.cream }}>
                        Browse sessions
                      </Button>
                    </Box>
                  </GridItem>
                ) : (
                  modulePreview.map((ch) => (
                    <Box
                      key={ch.id}
                      p={6}
                      bg="white"
                      borderWidth="1px"
                      borderColor={C.border}
                      borderRadius="lg"
                      position="relative"
                      overflow="hidden"
                    >
                      <Text fontSize="9px" fontWeight="800" color={C.primary} letterSpacing="0.1em" textTransform="uppercase" mb={2}>
                        Lesson
                      </Text>
                      <Heading size="sm" color={C.secondary} mb={3} fontFamily="'Tajawal','Amiri',sans-serif" noOfLines={2}>
                        {ch.name || ch.name_ar}
                      </Heading>
                      <Flex justify="space-between" align="center" pt={4} borderTopWidth="1px" borderColor="rgba(216,206,195,0.6)">
                        <Button
                          as={Link}
                          to="/videos/sections"
                          size="sm"
                          bg={C.border}
                          color={C.secondary}
                          fontSize="10px"
                          fontWeight="800"
                          textTransform="uppercase"
                          letterSpacing="0.06em"
                          _hover={{ bg: C.primary, color: C.cream }}
                        >
                          Resume
                        </Button>
                      </Flex>
                    </Box>
                  ))
                )}
              </SimpleGrid>
            </GridItem>

            <GridItem minW={0}>
              <VStack spacing={6} align="stretch">
                <Box p={6} bg={C.paper} borderWidth="1px" borderColor={C.border} borderRadius="lg">
                  <Text fontSize="10px" fontWeight="800" letterSpacing="0.15em" color={C.muted} textTransform="uppercase" mb={6}>
                    Learning analytics
                  </Text>
                  <Box p={4} bg="rgba(255,255,255,0.65)" borderWidth="1px" borderColor={C.border} borderRadius="lg" mb={4}>
                    <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase" mb={1}>
                      Total solved
                    </Text>
                    <Text fontSize="4xl" fontWeight="800" color={C.primary} lineHeight="1">
                      {totalSolved.toLocaleString()}
                    </Text>
                    <Text fontSize="9px" fontWeight="700" color={C.secondary} mt={2}>
                      Questions
                    </Text>
                  </Box>
                  <Box p={4} bg="rgba(255,255,255,0.65)" borderWidth="1px" borderColor={C.border} borderRadius="lg">
                    <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase" mb={1}>
                      Content covered
                    </Text>
                    <Flex align="center" gap={4}>
                      <Text fontSize="4xl" fontWeight="800" color={C.primary}>
                        {contentCoverage.pct}%
                      </Text>
                      <Box flex="1" bg={C.border} h="8px" borderRadius="full" overflow="hidden">
                        <Box bg={C.primary} h="full" w={`${contentCoverage.pct}%`} borderRadius="full" transition="width 0.3s" />
                      </Box>
                    </Flex>
                  </Box>
                  <Button
                    as={Link}
                    to="/performance-report"
                    w="full"
                    mt={6}
                    variant="outline"
                    borderWidth="2px"
                    borderColor={C.primary}
                    color={C.primary}
                    py={6}
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.1em"
                    textTransform="uppercase"
                    _hover={{ bg: C.primary, color: C.cream }}
                  >
                    Repeat practice
                  </Button>
                </Box>

              </VStack>
            </GridItem>
          </Grid>

          <Flex
            pt={8}
            pb={4}
            borderTopWidth="1px"
            borderColor={C.border}
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={6}
            align={{ base: "flex-start", md: "flex-end" }}
          >
            <Box maxW="md">
              <Text fontSize="10px" fontWeight="800" letterSpacing="0.12em" color={`${C.secondary}66`} textTransform="uppercase" mb={2}>
                Edutopia student portal
              </Text>
              <Text fontSize="sm" color={C.muted} lineHeight="tall">
                Your progress and attendance tools in one place. Use the full report for detailed chapter analytics.
              </Text>
            </Box>
            <ChakraLink
              as={Link}
              to="/privacy-policy"
              fontSize="9px"
              fontWeight="800"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={C.secondary}
              _hover={{ color: C.primary }}
            >
              Privacy
            </ChakraLink>
          </Flex>
        </Box>
      </Box>

      {/* <div className="fixed bottom-6 right-6 z-50">
        <button
          type="button"
          onClick={onChallengeOpen}
          className="relative bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
          style={{ width: "64px", height: "64px" }}
          title="Daily Challenge"
        >
          <Flame className="w-8 h-8 group-hover:scale-110 transition-transform" />
          {userData?.streak_count > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
              {userData.streak_count}
            </span>
          )}
        </button>
      </div> */}

      <AnnouncementsCarousel
        announcements={announcements}
        isOpen={showAnnouncements}
        onClose={handleCloseAnnouncements}
      />
      <DailyChallengeMenu isOpen={isChallengeOpen} onClose={onChallengeClose} userData={userData} />
    </StudentPortalShell>
  );
};

export default withTranslation()(StudentHome);
